import React, {
	FC,
	Suspense,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { ErrorBoundary } from "react-error-boundary";

import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { UserContext, TranslationContext } from "@/app/providers";
import { isImageAttachment } from "@/shared/lib/attachmentPreview";
import {
	AlertConfirmCompleteTask,
	AlertConfirmDeleteTask,
} from "@/shared/notifications/AlertConfirm";
import { AlertToast } from "@/shared/notifications/AlertToast";
import { DeleteActionButton, EditActionButton } from "@/shared/ui";
import { ImageAttachmentPreviewDialog } from "@/shared/ui/ImageAttachmentPreviewDialog";
import ComponentError from "@/shared/ui/errors/Components";
import {
	sharedTaskDialogActionsSx,
	sharedTaskDialogContentSx,
	sharedTaskDialogHeaderSx,
	sharedTaskDialogPanelSx,
	sharedTaskDialogPaperSx,
} from "@/styles/sharedTaskDialogStyles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import GroupsIcon from "@mui/icons-material/Groups";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
	Avatar,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Link,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Skeleton,
	TablePagination,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import dayjs from "dayjs";
import rehypeRaw from "rehype-raw";

import {
	useMutateCompleteTask,
	useMutateDeleteTask,
	useMutateDeleteTaskComment,
} from "@/service/TaskMutations";
import {
	useCreateTaskComment,
	useFetchRecurringChildTasks,
} from "@/service/TaskQueries";
import { Language } from "@/model/Language/Language";
import { SortDirection } from "@/model/SortDirection";


/* ─────────────── Tokens ─────────────── */
export const COL = {
	band: (t: Theme) =>
		t.palette.mode === "light" ? "#F8FAFC" : t.palette.background.default,
	chip: "#EEF2FF",
	chipText: "#3730A3",
};

const statusBg = (t: Theme, status?: string) => {
	const normalizedStatus =
		status === "NotStarted"
			? "Active"
			: status === "Archived"
				? "Completed"
				: status;
	const m: Record<string, string> = {
		Completed: t.palette.mode === "light" ? "#DCFCE7" : "#064E3B",
		Archived: t.palette.mode === "light" ? "#E5E7EB" : "#374151",
		Overdue: t.palette.mode === "light" ? "#FEE2E2" : "#7F1D1D",
		Active: t.palette.mode === "light" ? "#DBEAFE" : "#1E3A8A",
	};
	return normalizedStatus
		? (m[normalizedStatus] ??
			(t.palette.mode === "light" ? "#EFEFEF" : "#2F2F2F"))
		: t.palette.mode === "light"
			? "#EFEFEF"
			: "#2F2F2F";
};

const formatTaskStatusLabel = (status?: string) => {
	if (!status) return "—";
	if (status === "NotStarted") return "Active";
	if (status === "Archived") return "Completed";
	return status;
};

const stringToColor = (value: string) => {
	let hash = 0;
	for (let index = 0; index < value.length; index += 1) {
		hash = value.charCodeAt(index) + ((hash << 5) - hash);
	}

	let color = "#";
	for (let index = 0; index < 3; index += 1) {
		const channel = (hash >> (index * 8)) & 0xff;
		color += `00${channel.toString(16)}`.slice(-2);
	}

	return color;
};

const stringAvatar = (name: string) => ({
	sx: {
		bgcolor: stringToColor(name || "User"),
		width: 40,
		height: 40,
		fontSize: 16,
		fontWeight: 700,
	},
	children: (name || "U")
		.split(" ")
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0])
		.join("")
		.toUpperCase(),
});

interface TaskCommentUser {
	Id?: number;
	Name?: string;
	Email?: string;
}

interface TaskComment {
	CommentId?: number;
	Value?: string;
	Text?: string;
	Date?: string | Date;
	User?: TaskCommentUser | null;
}

interface Task {
	id?: number;
	title?: string;
	description?: string;
	attachment?: { Url: string; FileName: string } | null;
	manualCheckList?: boolean;
	user?: string;
	deadline?: string;
	lastChanged?: string | Date;
	frequency?: string;
	tags?: string[];
	shiftlog?: string;
	handover?: string;
	comments?: TaskComment[];

	/* extra fields your rows already carry */
	status?: string;
	completed?: string | null;
	completedBy?: TaskCommentUser | null;
	groupName?: string;
	selectedSystem?: number | null;
	groupResponsibleCount?: number;
	userResponsibleCount?: number;
	responsibleGroups?: string[];
	responsibleUsers?: string[];
	recurringTemplateId?: number | null;
	recurringChildren?: Array<{
		id: number;
		title: string;
		status?: string;
		deadline?: string | Date | null;
	}>;
}

interface TaskDialogProps {
	taskDialogOpen: boolean;
	selectedTask: Task | null;
	setTaskDialogOpen: (open: boolean) => void;
	renderInfoItem: (
		label: React.ReactNode,
		value: React.ReactNode,
	) => React.ReactNode;
	newComment?: string;
	setNewComment?: (comment: string) => void;
	isAdmin: boolean;
	setTaskIdToEdit?: (id: number | null) => void;
	viewOnly?: boolean;
	onNavigateTask?: (taskId: number, parentTaskId?: number | null) => void;
	recurringParentTaskId?: number | null;
	resolveTaskTitle?: (taskId: number) => string | undefined;
}

/** Compact identity line: avatar • name • email (inline) */
export const CompactIdentity: FC<{ name?: string; email?: string }> = ({
	name,
	email,
}) => {
	if (!name && !email) return <Typography color="text.secondary">—</Typography>;
	const initials =
		(name || "U")
			.split(" ")
			.map((n) => n[0])
			.join("")
			.toUpperCase() || "U";

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
			<Avatar sx={{ width: 18, height: 18, fontSize: 10 }}>{initials}</Avatar>
			<Typography variant="body2" noWrap sx={{ fontWeight: 600, minWidth: 0 }}>
				{name || "—"}
				{email ? (
					<Typography
						component="span"
						variant="caption"
						color="text.secondary"
						sx={{ ml: 0.75 }}
					>
						• {email}
					</Typography>
				) : null}
			</Typography>
		</Box>
	);
};

const TaskDialog: FC<TaskDialogProps> = ({
	taskDialogOpen,
	selectedTask,
	setTaskDialogOpen,
	renderInfoItem,
	newComment = "",
	setNewComment = () => {},
	isAdmin,
	viewOnly = false,
	setTaskIdToEdit = () => {},
	onNavigateTask,
	recurringParentTaskId = null,
	resolveTaskTitle,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const { timezone } = useTime();

	const currentUser = useContext(UserContext);

	const deleteTaskMutation = useMutateDeleteTask();
	const completeTaskMutation = useMutateCompleteTask();
	const { createTaskComment } = useCreateTaskComment();
	const deleteTaskCommentMutation = useMutateDeleteTaskComment();

	const [commentSubmitting, setCommentSubmitting] = useState(false);
	const [taskActionPending, setTaskActionPending] = useState<
		"delete" | "complete" | null
	>(null);
	const [deletingCommentId, setDeletingCommentId] = useState<number | null>(
		null,
	);
	const [comments, setComments] = useState<TaskComment[]>([]);
	const [imageAttachmentPreview, setImageAttachmentPreview] = useState<{
		url: string;
		fileName: string;
	} | null>(null);
	const [recurringChildrenPage, setRecurringChildrenPage] = useState(1);
	const [recurringChildrenPageSize, setRecurringChildrenPageSize] = useState(25);
	const [recurringChildrenSort, setRecurringChildrenSort] = useState<SortDirection>(
		SortDirection.ASC,
	);
	const safeNewComment = newComment ?? "";
	const sectionPanelSx = {
		...sharedTaskDialogPanelSx(theme),
		p: { xs: 1.5, md: 2 },
		height: "100%",
		display: "flex",
		flexDirection: "column",
		gap: 1.5,
	};
	const derivedParentTaskId =
		selectedTask?.recurringTemplateId &&
		selectedTask.recurringTemplateId !== selectedTask.id
			? selectedTask.recurringTemplateId
			: null;
	const parentTaskId =
		recurringParentTaskId && recurringParentTaskId !== selectedTask?.id
			? recurringParentTaskId
			: derivedParentTaskId;
	const isParentTask =
		recurringParentTaskId == null &&
		selectedTask?.recurring === "Yes" &&
		parentTaskId == null;
	const recurringChildrenQuery = useFetchRecurringChildTasks(
		selectedTask?.id ?? 0,
		recurringChildrenPage,
		recurringChildrenPageSize,
		recurringChildrenSort,
		taskDialogOpen && isParentTask,
	) as {
		data?: {
			Items?: Array<{
				TaskId?: number;
				Title?: string;
				Status?: string;
				Deadline?: string | Date | null;
			}>;
			Tasks?: Array<{
				TaskId?: number;
				Title?: string;
				Status?: string;
				Deadline?: string | Date | null;
			}>;
			Total?: number;
			HasNextPage?: boolean;
		};
		isFetching: boolean;
	};
	const recurringChildren = useMemo(() => {
		if (!isParentTask) return [];
		const items =
			recurringChildrenQuery.data?.Items ?? recurringChildrenQuery.data?.Tasks;
		if (!Array.isArray(items)) {
			return selectedTask?.recurringChildren ?? [];
		}
		return items
			.filter(
				(child): child is {
					TaskId: number;
					Title?: string;
					Status?: string;
					Deadline?: string | Date | null;
				} => Number.isInteger(child.TaskId) && child.TaskId > 0,
			)
			.filter((child) => Number(child.TaskId) !== Number(selectedTask?.id))
			.map((child) => ({
				id: child.TaskId,
				title: child.Title ?? "",
				status: child.Status ?? "",
				deadline: child.Deadline ?? null,
			}));
	}, [
		isParentTask,
		recurringChildrenQuery.data?.Items,
		recurringChildrenQuery.data?.Tasks,
		selectedTask?.id,
		selectedTask?.recurringChildren,
	]);
	const parentTaskTitle = parentTaskId
		? resolveTaskTitle?.(parentTaskId)
		: undefined;
	const activeChildrenCount = recurringChildren.filter(
		(child) => child.status === "NotStarted" || child.status === "Active",
	).length;
	const completedChildrenCount = recurringChildren.filter(
		(child) => child.status === "Completed" || child.status === "Archived",
	).length;
	const recurringChildrenIncludesCurrentTask = Boolean(
		recurringChildrenQuery.data?.Items?.some(
			(child) => Number(child.TaskId) === Number(selectedTask?.id),
		) ||
			recurringChildrenQuery.data?.Tasks?.some(
				(child) => Number(child.TaskId) === Number(selectedTask?.id),
			),
	);
	const totalRecurringChildren = isParentTask
		? Math.max(
				(recurringChildrenQuery.data?.Total ?? recurringChildren.length) -
					(recurringChildrenIncludesCurrentTask ? 1 : 0),
				recurringChildren.length,
			)
		: recurringChildren.length;
	const recurringChildrenHasNextPage = Boolean(
		recurringChildrenQuery.data?.HasNextPage,
	);
	const isCompletedTask =
		selectedTask?.status === "Completed" || selectedTask?.status === "Archived";
	const canCompleteTask =
		!isParentTask && !isCompletedTask && selectedTask?.status !== undefined;
	const taskLabel = GetLanguageValueAsString(
		translation,
		"TaskDialogRecurring.Task",
		"Task",
	);
	const taskWordLabel = GetLanguageValueAsString(
		translation,
		totalRecurringChildren === 1
			? "TaskDialogRecurring.TaskSingular"
			: "TaskDialogRecurring.TaskPlural",
		totalRecurringChildren === 1 ? "task" : "tasks",
	);
	const untitledTaskLabel = GetLanguageValueAsString(
		translation,
		"TaskDialogRecurring.Untitled",
		"Untitled",
	);
	const noDeadlineLabel = GetLanguageValueAsString(
		translation,
		"TaskDialogRecurring.NoDeadline",
		"No deadline",
	);
	const sortLabel = GetLanguageValueAsString(
		translation,
		"TaskDialogRecurring.Sort",
		"Sort",
	);
	const sortAscendingLabel = GetLanguageValueAsString(
		translation,
		"TaskDialogRecurring.SortAscending",
		"ASC",
	);
	const sortDescendingLabel = GetLanguageValueAsString(
		translation,
		"TaskDialogRecurring.SortDescending",
		"DESC",
	);
	const recurringChildrenSortLabel = `${sortLabel}: ${
		recurringChildrenSort === SortDirection.ASC
			? sortAscendingLabel
			: sortDescendingLabel
	}`;

	useEffect(() => {
		if (Array.isArray(selectedTask?.comments)) {
			setComments(selectedTask.comments);
		} else {
			setComments([]);
		}
	}, [selectedTask]);

	useEffect(() => {
		setRecurringChildrenPage(1);
	}, [selectedTask?.id]);

	useEffect(() => {
		setRecurringChildrenPage(1);
	}, [recurringChildrenSort]);

	// Derive nice labels for responsibles (users + groups)
	const usersList = selectedTask?.responsibleUsers ?? [];
	const groupsList = selectedTask?.responsibleGroups ?? [];
	const displayUsersList = Array.from(
		new Set(usersList.filter((name): name is string => Boolean(name))),
	);
	const displayGroupsList = Array.from(
		new Set(
			[selectedTask?.groupName, ...groupsList].filter(
				(name): name is string => Boolean(name),
			),
		),
	);

	const usersCount =
		displayUsersList.length ||
		selectedTask?.userResponsibleCount ||
		(selectedTask?.user ? 1 : 0) ||
		0;
	const groupsCount =
		displayGroupsList.length ||
		selectedTask?.groupResponsibleCount ||
		(selectedTask?.groupName ? 1 : 0) ||
		0;

	/* ─────────────── Handlers ─────────────── */
	const handleDeleteTask = async () => {
		if (taskActionPending) return;
		if (!selectedTask?.id) return;
		setTaskActionPending("delete");
		try {
			await deleteTaskMutation.mutateAsync(selectedTask.id);
			await AlertToast.success("Task deleted successfully.");
			setTaskDialogOpen(false);
		} catch {
			await AlertToast.failed("Failed to delete task.");
		} finally {
			setTaskActionPending(null);
		}
	};

	const handleCompleteTask = async () => {
		if (taskActionPending) return;
		if (!selectedTask?.id) return;
		setTaskActionPending("complete");
		try {
			await completeTaskMutation.mutateAsync({
				id: selectedTask.id,
				payload: {
					groupId: 0,
					taskId: selectedTask.id,
					toHandover: selectedTask.handover === "Yes",
					toShift: selectedTask.shiftlog === "Yes",
					status: "Completed",
					completed: new Date().toISOString(),
				},
			});
			await AlertToast.success("Task confirmed successfully.");
			if (parentTaskId && onNavigateTask) {
				onNavigateTask(parentTaskId, null);
			} else {
				setTaskDialogOpen(false);
			}
		} catch {
			await AlertToast.failed("Failed to confirm task.");
		} finally {
			setTaskActionPending(null);
		}
	};

	const handleSendComment = async () => {
		if (!safeNewComment.trim() || !selectedTask?.id) return;
		setCommentSubmitting(true);
		try {
			await createTaskComment({
				TaskId: selectedTask.id,
				Comment: safeNewComment,
			});
			const newCommentObj = {
				Value: safeNewComment,
				Date: new Date().toISOString(),
				User: { Name: currentUser?.Name || "Unknown" },
			} satisfies TaskComment;
			setComments((prev) => [...prev, newCommentObj]);
			setNewComment("");
			await AlertToast.success("Comment posted.");
		} catch {
			await AlertToast.failed("Failed to post comment.");
		} finally {
			setCommentSubmitting(false);
		}
	};

	const handleDeleteComment = async (commentId?: number) => {
		if (!commentId || !selectedTask?.id) return;
		if (deletingCommentId) return;
		setDeletingCommentId(commentId);
		try {
			setComments((prev) => prev.filter((c) => c.CommentId !== commentId));
			await deleteTaskCommentMutation.mutateAsync({
				commentId,
				taskId: selectedTask.id,
			});
			await AlertToast.success("Comment deleted.");
		} catch {
			await AlertToast.failed("Failed to delete comment.");
		} finally {
			setDeletingCommentId(null);
		}
	};

	const copyId = async () => {
		if (!selectedTask?.id) return;
		try {
			await navigator.clipboard.writeText(String(selectedTask.id));
			await AlertToast.success("Task ID copied.");
		} catch {
			await AlertToast.failed("Could not copy Task ID.");
		}
	};

	/* ─────────────── Loading Skeleton ─────────────── */
	if (!selectedTask && taskDialogOpen) {
		return (
			<ErrorBoundary
				fallbackRender={({ error, resetErrorBoundary }) => (
					<ComponentError
						message={
							error instanceof Error ? error.message : "Something went wrong."
						}
						reset={resetErrorBoundary}
					/>
				)}
			>
				<Suspense fallback={<CircularProgress sx={{ m: 4 }} />}>
					<Dialog
						open={taskDialogOpen}
						onClose={() => setTaskDialogOpen(false)}
						fullScreen={isMobile}
						maxWidth="xs"
						fullWidth
						PaperProps={{
							sx: {
								...sharedTaskDialogPaperSx(theme, isMobile),
							},
						}}
					>
						<DialogTitle
							sx={{
								...sharedTaskDialogHeaderSx(theme),
								px: 2.5,
								pr: 7,
								py: 1.5,
							}}
						>
							<Typography variant="h6" fontWeight={900}>
								<Translate String="Auto.Features.Task.Ui.Components.TaskDialogRecurring.L413C50.LoadingTask">Loading task…</Translate>
							</Typography>
							<IconButton
								aria-label={GetLanguageValueAsString(translation, "Auto.Features.Task.Ui.Components.TaskDialogRecurring.L417C20.Close", "Close")}
								onClick={() => setTaskDialogOpen(false)}
								size="small"
								disabled={Boolean(taskActionPending)}
							>
								<CloseIcon fontSize="small" />
							</IconButton>
						</DialogTitle>
						<DialogContent
							dividers
							sx={{ ...sharedTaskDialogContentSx(theme) }}
						>
							<Grid container spacing={2}>
								{Array.from({ length: 6 }).map((_, idx) => (
									<Grid item xs={12} key={idx}>
										<Skeleton variant="rectangular" height={40} />
									</Grid>
								))}
							</Grid>
						</DialogContent>
					</Dialog>
				</Suspense>
			</ErrorBoundary>
		);
	}

	/* ─────────────── Main Dialog ─────────────── */
	return (
		<>
			<Dialog
				open={taskDialogOpen}
				onClose={() => setTaskDialogOpen(false)}
				fullScreen={isMobile}
				maxWidth="md"
				fullWidth
				PaperProps={{
					sx: { ...sharedTaskDialogPaperSx(theme, isMobile) },
				}}
			>
				{/* Header */}
				<DialogTitle
					sx={{
						...sharedTaskDialogHeaderSx(theme),
						pr: 7,
						pl: { xs: 2, md: 3 },
						py: 1.5,
						display: "flex",
						alignItems: "center",
						gap: 1,
						flexWrap: "wrap",
					}}
				>
					<Typography variant="h6" fontWeight={900}>
						<Translate String="Auto.Features.Task.Ui.Components.TaskDialogRecurring.L469C48.Task">Task #</Translate>{selectedTask?.id}
					</Typography>

					{/* quick header chips */}
					{!!selectedTask?.status && (
						<Chip
							size="small"
							icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
							label={formatTaskStatusLabel(selectedTask.status)}
							sx={(t) => ({
								bgcolor: statusBg(t, selectedTask.status),
								fontWeight: 800,
							})}
						/>
					)}
					{!!selectedTask?.groupName && (
						<Chip
							size="small"
							icon={<GroupsIcon sx={{ fontSize: 16 }} />}
							label={selectedTask.groupName}
							variant="outlined"
						/>
					)}
					<Box sx={{ ml: "auto" }} />
					<Tooltip title={GetLanguageValueAsString(translation, "Auto.Features.Task.Ui.Components.TaskDialogRecurring.L494C21.CopyTaskID", "Copy Task ID")}>
						<IconButton
							onClick={() => {
								void copyId();
							}}
							size="small"
						>
							<ContentCopyIcon fontSize="small" />
						</IconButton>
					</Tooltip>

					<IconButton
						aria-label={GetLanguageValueAsString(translation, "Auto.Features.Task.Ui.Components.TaskDialogRecurring.L506C18.Close", "Close")}
						onClick={() => setTaskDialogOpen(false)}
						size="small"
						disabled={Boolean(taskActionPending)}
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				{/* Body */}
				<DialogContent
					dividers
					sx={{
						...sharedTaskDialogContentSx(theme),
						p: { xs: 2, md: 3 },
						overflowY: "auto",
						WebkitOverflowScrolling: "touch",
						flex: 1,
						minHeight: 0,
					}}
				>
					<Grid container spacing={2}>
						<Grid item xs={12} md={7}>
							<Box sx={sectionPanelSx}>
								{renderInfoItem(
									<Translate String="TaskDialogRecurring.Title">Title</Translate>,
									selectedTask?.title || "N/A",
								)}
								<Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, pt: 1.5 }}>
									{renderInfoItem(
										<Translate String="TaskDialogRecurring.Description">
											Description
										</Translate>,
										selectedTask?.description ? (
											<SATOSContentRender
												isNotProcedureRender={true}
												rehypePlugins={[rehypeRaw]}
												content={selectedTask.description}
											/>
										) : (
											"N/A"
										),
									)}
								</Box>
								<Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, pt: 1.5 }}>
									<Typography variant="subtitle2" color="textSecondary">
										<Translate String="TaskDialogRecurring.Attachment">
											Attachment
										</Translate>
									</Typography>
									{selectedTask?.attachment ? (
										<Box display="flex" alignItems="center" gap={1} mt={1}>
											<ArrowOutwardIcon fontSize="small" />
											<Link
												href={selectedTask.attachment.Url}
												target={
													isImageAttachment(selectedTask.attachment)
														? undefined
														: "_blank"
												}
												rel="noopener noreferrer"
												onClick={(event) => {
													if (!isImageAttachment(selectedTask.attachment)) return;
													event.preventDefault();
													setImageAttachmentPreview({
														url: selectedTask.attachment.Url,
														fileName:
															selectedTask.attachment.FileName ||
															"Image attachment",
													});
												}}
											>
												{selectedTask.attachment.FileName}
											</Link>
										</Box>
									) : (
										<Typography color="textSecondary" mt={1}>
											<Translate String="TaskDialogRecurring.NoAttachment">
												No attachment
											</Translate>
										</Typography>
									)}
								</Box>
							</Box>
						</Grid>

						<Grid item xs={12} md={5}>
							<Box sx={sectionPanelSx}>
								<Grid container spacing={1.5}>
									<Grid item xs={12}>
										{renderInfoItem(
											<Translate String="TaskDialogRecurring.User">User</Translate>,
											<CompactIdentity
												name={selectedTask?.user}
												email={undefined}
											/>,
										)}
									</Grid>
									<Grid item xs={12}>
										{/* {renderInfoItem(
											<>
												<CalendarMonthIcon fontSize="small" sx={{ mr: 0.75 }} />
												<Translate String="TaskDialogRecurring.Deadline">
													Deadline
												</Translate>
											</>,
											selectedTask?.deadline
												? dayjs
														.utc(selectedTask.deadline)
														.tz(timezone)
														.format("DD/MM/YY HH:mm:ss")
												: "—",
										)} */}
									</Grid>
									<Grid item xs={12}>
										{/* {renderInfoItem(
											<>
												<AccessTimeIcon fontSize="small" sx={{ mr: 0.75 }} />
												<Translate String="TaskDialogRecurring.LastChanged">
													Last Changed
												</Translate>
											</>,
											selectedTask?.lastChanged
												? dayjs
														.utc(selectedTask.lastChanged)
														.tz(timezone)
														.format("DD/MM/YY HH:mm:ss")
												: "—",
										)} */}
									</Grid>
									<Grid item xs={12}>
										{renderInfoItem(
											<Translate String="TaskDialogRecurring.Recurring">
												Recurring
											</Translate>,
											selectedTask?.frequency || "N/A",
										)}
									</Grid>
									<Grid item xs={12}>
										{renderInfoItem(
											<Translate String="TaskDialogRecurring.Status">
												Status
											</Translate>,
											<Chip
												size="small"
												label={formatTaskStatusLabel(selectedTask?.status)}
												sx={(t) => ({
													bgcolor: statusBg(t, selectedTask?.status),
													fontWeight: 800,
												})}
											/>,
										)}
									</Grid>
								</Grid>
							</Box>
						</Grid>

						<Grid item xs={12}>
							<Box sx={sectionPanelSx}>
								{renderInfoItem(
									<Translate String="TaskDialogRecurring.Responsibles">
										Responsibles
									</Translate>,
									<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
										<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>
												<Translate String="TaskDialogRecurring.Users">Users</Translate>:
											</Typography>
											{usersCount === 0 ? (
												<Typography variant="body2" color="text.secondary">
													<Translate String="TaskDialogRecurring.None">None</Translate>
												</Typography>
											) : (
												<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
													{(displayUsersList.length
														? displayUsersList
														: [selectedTask?.user])
														.filter(Boolean)
														.map((name, idx) => (
															<Chip
																key={`${name}-${idx}`}
																label={name}
																size="small"
																variant="outlined"
															/>
														))}
												</Box>
											)}
										</Box>
										<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>
												<Translate String="TaskDialogRecurring.Groups">Groups</Translate>:
											</Typography>
											{groupsCount === 0 ? (
												<Typography variant="body2" color="text.secondary">
													<Translate String="TaskDialogRecurring.None">None</Translate>
												</Typography>
											) : (
												<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
													{displayGroupsList
														.filter(Boolean)
														.map((name, idx) => (
															<Chip
																key={`${name}-${idx}`}
																label={name}
																size="small"
																variant="outlined"
															/>
														))}
												</Box>
											)}
										</Box>
									</Box>,
								)}

								{selectedTask?.tags?.length ? (
									<Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, pt: 1.5 }}>
										<Typography variant="subtitle2" color="textSecondary">
											<Translate String="TaskDialogRecurring.Tags">Tags</Translate>
										</Typography>
										<Box display="flex" flexWrap="wrap" gap={1} mt={1}>
											{selectedTask.tags.map((tag, i) => (
												<Chip
													key={i}
													label={tag}
													size="small"
													sx={{
														bgcolor: COL.chip,
														color: COL.chipText,
														fontWeight: 600,
													}}
												/>
											))}
										</Box>
									</Box>
								) : null}

								<Box
									sx={{
										borderTop: `1px solid ${theme.palette.divider}`,
										pt: 1.5,
										display: "grid",
										gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
										gap: 2,
									}}
								>
									<Box>
										<Typography variant="subtitle2">
											<Translate String="TaskDialogRecurring.Shiftlog">
												Shiftlog
											</Translate>
										</Typography>
										<Checkbox checked={selectedTask?.shiftlog === "Yes"} disabled />
									</Box>
									<Box>
										<Typography variant="subtitle2">
											<Translate String="TaskDialogRecurring.Handover">
												Handover
											</Translate>
										</Typography>
										<Checkbox checked={selectedTask?.handover === "Yes"} disabled />
									</Box>
								</Box>

								{(parentTaskId || isParentTask) && (
									<Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, pt: 1.5 }}>
										<Typography
											variant="subtitle2"
											sx={{ fontWeight: 800, mb: 1.25 }}
										>
											<Translate String="TaskDialogRecurring.RecurringRelationships">
												Recurring relationships
											</Translate>
										</Typography>
										{parentTaskId ? (
											<Box sx={{ mb: recurringChildren.length > 0 ? 1.5 : 0 }}>
												<Typography
													variant="caption"
													color="text.secondary"
													sx={{ display: "block", mb: 0.5 }}
												>
													<Translate String="TaskDialogRecurring.TemplateTask">
														Template task
													</Translate>
												</Typography>
												<Button
													variant="outlined"
													size="small"
													onClick={() => onNavigateTask?.(parentTaskId, null)}
													sx={{
														borderRadius: 2,
														textTransform: "none",
														justifyContent: "flex-start",
														fontWeight: 700,
													}}
												>
													{taskLabel} #{parentTaskId}
													{parentTaskTitle ? ` · ${parentTaskTitle}` : ""}
												</Button>
											</Box>
										) : null}
											{isParentTask ? (
												<Box>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														justifyContent: "space-between",
														gap: 1,
														flexWrap: "wrap",
														mb: 0.75,
													}}
												>
													<Box>
														<Typography
															variant="caption"
															color="text.secondary"
															sx={{ display: "block", mb: 0.25 }}
														>
															<Translate String="TaskDialogRecurring.Children">
																Children
															</Translate>
														</Typography>
														<Typography variant="body2" sx={{ fontWeight: 700 }}>
															{totalRecurringChildren} {taskWordLabel}
														</Typography>
													</Box>
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															gap: 0.75,
															flexWrap: "wrap",
														}}
													>
														<Button
															variant="outlined"
															size="small"
															onClick={() =>
																setRecurringChildrenSort((previous) =>
																	previous === SortDirection.ASC
																		? SortDirection.DESC
																		: SortDirection.ASC,
																)
															}
															startIcon={
																<ArrowUpwardIcon
																	sx={{
																		fontSize: 14,
																		transform:
																			recurringChildrenSort === SortDirection.DESC
																				? "rotate(180deg)"
																				: "rotate(0deg)",
																		transition: "transform 120ms ease",
																	}}
																/>
															}
															sx={{
																minHeight: 28,
																px: 1,
																py: 0.25,
																borderRadius: 2,
																textTransform: "none",
																fontWeight: 700,
																fontSize: 12,
																color: "primary.main",
																borderColor: theme.palette.primary.main,
																bgcolor:
																	theme.palette.mode === "light"
																		? "rgba(25, 118, 210, 0.06)"
																		: "rgba(144, 202, 249, 0.12)",
																"&:hover": {
																	borderColor: theme.palette.primary.dark,
																	bgcolor:
																		theme.palette.mode === "light"
																			? "rgba(25, 118, 210, 0.12)"
																			: "rgba(144, 202, 249, 0.18)",
																},
																"& .MuiButton-startIcon": {
																	mr: 0.5,
																	ml: 0,
																},
															}}
														>
															{recurringChildrenSortLabel}
														</Button>
															<Chip
																size="small"
																label={`${activeChildrenCount} active`}
																variant="outlined"
															/>
															<Chip
																size="small"
																label={`${completedChildrenCount} completed`}
																variant="outlined"
															/>
														</Box>
													</Box>
													{recurringChildrenQuery.isFetching ? (
														<Box
															sx={{
																display: "flex",
																alignItems: "center",
																justifyContent: "center",
																minHeight: 120,
															}}
														>
															<CircularProgress size={24} />
														</Box>
													) : (
														<>
															<Box
																sx={{
																	display: "flex",
																	flexDirection: "column",
																	gap: 0.75,
																	maxHeight: 280,
																	overflowY: "auto",
																	pr: 0.25,
																}}
															>
																{recurringChildren.map((child) => (
																	<Box
																		key={child.id}
																		sx={{
																			display: "flex",
																			alignItems: "center",
																			justifyContent: "space-between",
																			gap: 1,
																			border: `1px solid ${theme.palette.divider}`,
																			borderRadius: 2,
																			px: 1.25,
																			py: 1,
																			bgcolor: "background.default",
																		}}
																	>
																		<Box
																			sx={{
																				minWidth: 0,
																				display: "flex",
																				flexDirection: "column",
																				gap: 0.35,
																			}}
																		>
																			<Button
																				variant="text"
																				onClick={() =>
																					onNavigateTask?.(
																						child.id,
																						selectedTask?.id ?? null,
																					)
																				}
																				sx={{
																					p: 0,
																					minWidth: 0,
																					textTransform: "none",
																					fontWeight: 700,
																					justifyContent: "flex-start",
																				maxWidth: "100%",
																			}}
																		>
																			{taskLabel} #{child.id} · {child.title || untitledTaskLabel}
																		</Button>
																			<Typography
																				variant="caption"
																				color="text.secondary"
																				noWrap
																			>
																				{child.deadline
																					? `${dayjs
																				.utc(child.deadline)
																				.tz(timezone)
																				.format("DD/MM/YY HH:mm")}`
																		: noDeadlineLabel}
																	</Typography>
																		</Box>
																		<Box
																			sx={{
																				display: "flex",
																				alignItems: "center",
																				gap: 0.75,
																				flexShrink: 0,
																			}}
																		>
																			{child.status ? (
																				<Chip
																					size="small"
																					label={formatTaskStatusLabel(child.status)}
																					sx={(t) => ({
																						bgcolor: statusBg(t, child.status),
																						fontWeight: 800,
																					})}
																				/>
																			) : null}
																		</Box>
																	</Box>
																))}
															</Box>
															<TablePagination
																component="div"
																count={totalRecurringChildren}
																page={Math.max(recurringChildrenPage - 1, 0)}
																onPageChange={(_, newPage) =>
																	setRecurringChildrenPage(newPage + 1)
																}
																rowsPerPage={recurringChildrenPageSize}
																onRowsPerPageChange={(event) => {
																	setRecurringChildrenPageSize(
																		Number(event.target.value),
																	);
																	setRecurringChildrenPage(1);
																}}
																rowsPerPageOptions={[25, 50, 100]}
																labelRowsPerPage={GetLanguageValueAsString(
																	translation,
																	"TaskDialogRecurring.RowsPerPage",
																	"Rows per page:",
																)}
																labelDisplayedRows={({ from, to, count }) =>
																	count !== -1 ? `${from}-${to} of ${count}` : `${from}-${to}`
																}
																nextIconButtonProps={{
																	disabled: !recurringChildrenHasNextPage,
																}}
																sx={{
																	mt: 1,
																	px: 0,
																	".MuiTablePagination-toolbar": {
																		px: 0,
																	},
																}}
															/>
														</>
													)}
												</Box>
											) : null}
									</Box>
								)}
							</Box>
						</Grid>

						<Grid item xs={12}>
							<Box sx={sectionPanelSx}>
								<Typography variant="subtitle2">
									<Translate String="TaskDialogRecurring.Comments">
										Comments
									</Translate>
								</Typography>
								<Box
									sx={{
										maxHeight: 220,
										overflowY: "auto",
										display: "flex",
										flexDirection: "column",
										gap: 1.5,
										p: 0,
									}}
								>
									{comments.length > 0 ? (
										<List
											dense
											sx={{
												border: `1px solid ${theme.palette.divider}`,
												borderRadius: 2,
												p: 1.5,
											}}
										>
											{comments
												.slice()
												.reverse()
												.map((comment, idx) => (
													<ListItem
														key={comment.CommentId ?? idx}
														alignItems="flex-start"
														sx={{ px: 0, py: 1 }}
													>
														<ListItemAvatar>
															<Tooltip title={comment?.User?.Name || ""}>
																<Avatar
																	{...stringAvatar(comment?.User?.Name ?? "")}
																	alt={comment?.User?.Name}
																/>
															</Tooltip>
														</ListItemAvatar>
														<ListItemText
															primary={
																<Box
																	sx={{
																		...sharedTaskDialogPanelSx(theme),
																		px: 2,
																		py: 1,
																		borderRadius: 2,
																		display: "inline-block",
																		maxWidth: "100%",
																		backgroundColor:
																			theme.palette.mode === "dark"
																				? alpha(theme.palette.common.white, 0.03)
																				: alpha(theme.palette.common.black, 0.01),
																	}}
																>
																	<SATOSContentRender
																		isNotProcedureRender={true}
																		rehypePlugins={[rehypeRaw]}
																		content={comment.Value ?? comment.Text}
																	/>
																</Box>
															}
															secondary={
																<Typography
																	variant="caption"
																	color="text.secondary"
																	sx={{ display: "block", mt: 0.5 }}
																>
																	Created by {comment?.User?.Name || "Unknown"} on {comment?.Date ? dayjs.utc(comment.Date).tz(timezone).format("DD/MM/YY HH:mm:ss") : "—"}
																</Typography>
															}
														/>
														{(isAdmin ||
															comment?.User?.Name === selectedTask?.user) && (
															<IconButton
																size="small"
																onClick={() => {
																	void handleDeleteComment(comment?.CommentId);
																}}
																disabled={
																	Boolean(deletingCommentId) ||
																	!comment?.CommentId
																}
															>
																{deletingCommentId === comment?.CommentId ? (
																	<CircularProgress size={14} color="inherit" />
																) : (
																	<DeleteIcon fontSize="small" />
																)}
															</IconButton>
														)}
													</ListItem>
												))}
										</List>
									) : (
										<Typography variant="body2" color="text.secondary">
											<Translate String="TaskDialogRecurring.NoComments">
												No comments
											</Translate>
										</Typography>
									)}
								</Box>

								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										px: 1,
										py: 0.5,
										border: `1px solid ${theme.palette.divider}`,
										borderRadius: 2,
										bgcolor: "background.default",
									}}
								>
									<TextField
										value={safeNewComment}
										onChange={(e) => setNewComment(e.target.value)}
										placeholder={GetLanguageValueAsString(translation, "Auto.Features.Task.Ui.Components.TaskDialogRecurring.L1073C23.TypeAComment", "Type a comment...")}
										variant="standard"
										multiline
										maxRows={3}
										InputProps={{ disableUnderline: true, sx: { fontSize: 14 } }}
										sx={{ flex: 1, mr: 1 }}
									/>
									<IconButton
										color="primary"
										onClick={() => {
											void handleSendComment();
										}}
										disabled={!safeNewComment.trim() || commentSubmitting}
									>
										{commentSubmitting ? (
											<CircularProgress size={20} />
										) : (
											<ArrowUpwardIcon />
										)}
									</IconButton>
								</Box>
							</Box>
						</Grid>
					</Grid>
				</DialogContent>

				{/* Footer */}

				{!viewOnly && (
					<DialogActions
						sx={{
							...sharedTaskDialogActionsSx(theme, isMobile),
							py: 1.25,
							px: 2,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 1,
							flexWrap: "wrap",
						}}
					>
						{/* Left: admin actions */}
						<Box sx={{ display: "flex", gap: 1 }}>
							{isAdmin && (
								<>
									<EditActionButton
										onClick={() => {
											setTaskDialogOpen(false);
											setTaskIdToEdit(selectedTask?.id ?? null);
										}}
										startIcon={<EditIcon />}
										sx={{
											borderRadius: 2,
											textTransform: "none",
											fontWeight: 800,
										}}
									>
										<Translate String="TaskDialogRecurring.Edit">
											Edit
										</Translate>
									</EditActionButton>
									<DeleteActionButton
										sx={{
											borderRadius: 2,
											textTransform: "none",
											fontWeight: 800,
										}}
										variant="outlined"
										color="error"
										startIcon={
											taskActionPending === "delete" ? (
												<CircularProgress size={16} color="inherit" />
											) : (
												<DeleteIcon />
											)
										}
										onClick={() => {
											AlertConfirmDeleteTask(() => {
												void handleDeleteTask();
											});
										}}
										disabled={Boolean(taskActionPending)}
									>
										{taskActionPending === "delete" ? (
											<Translate String="TaskDialogRecurring.Deleting">
												Deleting...
											</Translate>
										) : (
											<Translate String="TaskDialogRecurring.Delete">
												Delete
											</Translate>
										)}
									</DeleteActionButton>
								</>
							)}
						</Box>


						{/* Right: primary confirm */}
						<Box sx={{ display: "flex", gap: 1 }}>
							{canCompleteTask && (
								<Button
									sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
									variant="contained"
									color="success"
									startIcon={
										taskActionPending === "complete" ? (
											<CircularProgress size={16} color="inherit" />
										) : (
											<CheckCircleIcon />
										)
									}
									onClick={() => {
										AlertConfirmCompleteTask(() => {
											void handleCompleteTask();
										});
									}}
									disabled={Boolean(taskActionPending)}
								>
									{taskActionPending === "complete" ? (
										<Translate String="TaskDialogRecurring.Completing">
											Completing...
										</Translate>
									) : (
										<Translate String="TaskDialogRecurring.Complete">
											Complete
										</Translate>
									)}
								</Button>
							)}
						</Box>

					</DialogActions>
				)}
			</Dialog>
			<ImageAttachmentPreviewDialog
				preview={imageAttachmentPreview}
				onClose={() => setImageAttachmentPreview(null)}
			/>
		</>
	);
};

export default TaskDialog;
